// redux/brandSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { BrandDetails } from '../types/brand';
import AppConfig, { setTenantId } from '../lib/AppConfig';

/**
 * Fetch brand config using native fetch()
 * Retries 3 times, returns BrandDetails or rejectValue<string>
 */
export const fetchBrand = createAsyncThunk<BrandDetails, void, { rejectValue: string }>(
	'brand/fetch',
	async (_, { rejectWithValue }) => {
		const MAX_RETRIES = 3;
		const domain = window.location.hostname;

		let lastError: unknown = null;

		for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
			try {
				const url = `${AppConfig.api_url}/gmapis/tenants/brand?domain=${encodeURIComponent(domain)}`;
				// if relative, server must resolve gmapis/... path

				const res = await fetch(url, {
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
				});

				if (!res.ok) throw new Error(`HTTP ${res.status}`);

				const data = (await res.json()) as BrandDetails;

				setTenantId(data.identifier);

				return data;
			} catch (err) {
				lastError = err;
				await new Promise(r => setTimeout(r, 1000)); // retry backoff
				if (attempt === MAX_RETRIES) break;
			}
		}

		return rejectWithValue(
			lastError instanceof Error ? lastError.message : 'Failed fetching brand',
		);
	},
);

// ---------------- STATE ---------------- //

export interface BrandState {
	data?: BrandDetails | null;
	loading: boolean;
	error: string | null;
}

const initialState: BrandState = {
	data: null,
	loading: true,
	error: null,
};

// ---------------- SLICE ---------------- //

const brandSlice = createSlice({
	name: 'brand',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder
			.addCase(fetchBrand.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchBrand.fulfilled, (state, action: PayloadAction<BrandDetails>) => {
				state.data = action.payload;
				state.loading = false;
			})
			.addCase(fetchBrand.rejected, (state, action) => {
				state.loading = false;
				state.data = null;
				state.error = (action.payload as string) ?? 'Unable to load brand config';
			});
	},
});

export default brandSlice.reducer;
